import React, {Fragment} from 'react';
import 'font-awesome/css/font-awesome.min.css';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';

import {getVendor, getSettings, getReservationsByVendorId} from '../data/get-from-sheet';

import upsertReservation from './upsertReservation';
import validateForm from './validate';
import './eventPage.css';
import Loading from './Loading';
import Legal from './Legal';
import SpotsSection from './SpotsSection';
import ReservationList from './ReservationList';
import {isSettingEnabled} from './setting-enabled';

const markAsAcknowledgedIfCheckboxNotShown = ({agreeToAbide = { }}) =>
    !isSettingEnabled(agreeToAbide.show);

class EventPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            acknowledged: false,
            attendee: {
                vendorId: props.match.params.vendorId,
                vendorName: '',
                firstName: '',
                lastName: '',
                textField1: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                email: '',
                phone: '',
                textField2: '',
                attendeeId: undefined
            },
            reservations: [],
            errors: undefined,
            loading: true,
            rsvpInProgress: false,
            sheetId: props.match.params.sheetId,
            editing: false
        };
    }

    async componentDidMount() {
        const vendorPromise = getVendor(this.state.sheetId, this.state.attendee.vendorId);
        const settingsPromise = getSettings(this.state.sheetId);
        const reservationsPromise = getReservationsByVendorId(this.state.sheetId, this.state.attendee.vendorId);
        const [settings, vendor, reservations] = await Promise.all([settingsPromise, vendorPromise, reservationsPromise]);

        this.setState((oldState) => ({
            acknowledged: markAsAcknowledgedIfCheckboxNotShown(settings.form),
            attendee: {
                ...oldState.attendee,
                vendorName: vendor.name
            },
            loading: false,
            settings,
            vendor,
            reservations,
            errors: validateForm(oldState.attendee, settings.form.fields)
        }));
    }

    onFieldChangeGenerator = (fieldName) =>
        ({target: {value}}) =>
            this.updateAttendee({[fieldName]: value});

    updateAttendee = (attendeeChange) =>
        this.setState((oldState) => {
            const attendee = {
                ...oldState.attendee,
                ...attendeeChange
            };

            const errors = validateForm(attendee, oldState.settings.form.fields);

            return {
                attendee,
                errors
            };
        });

    editAttendee = (attendee) => {
        this.setState({editing: true});
        this.updateAttendee(attendee);
    }

    onAcknowledgeClicked = (event) => {
        const acknowledged = event.target.checked;

        this.updateAttendee();

        this.setState(() => ({
            acknowledged
        }));
    }

    agreeToAbideSection = () => {
        const {agreeToAbide = {}} = this.state.settings.form;

        if (isSettingEnabled(agreeToAbide.show)) {
            return (
                <Fragment>
                    <p className="heavy">{agreeToAbide.label}</p>
                    <label className="heavy">
                        <input
                            checked={this.state.acknowledged}
                            className="inline"
                            onChange={this.onAcknowledgeClicked}
                            type="checkbox"
                        />
                        {agreeToAbide.text}
                    </label>
                </Fragment>
            );
        }

        return null;
    }

    onSubmit = async (event) => {
        event.preventDefault();

        this.setState({rsvpInProgress: true});

        const reservationId = await upsertReservation(this.state.sheetId, this.state.attendee);

        this.props.history.push(`/event/${this.state.sheetId}/${this.state.attendee.vendorId}/${reservationId}/thankyou`);
    }

    formIncomplete = () => Boolean(
        this.state.errors ||
        this.state.rsvpInProgress ||
        !this.state.acknowledged);

    inquirySection = () => {
        const {contact = {}} = this.state.settings;

        if (contact.email && contact.name && contact.phone) {
            return (
                <p>
                    {`Hy-Vee inquiries can be directed to ${contact.name} at ${contact.phone} or via e-mail at `}
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </p>
            );
        }

        return null;
    }

    questionsSection = () => {
        const contact = this.state.vendor.contact;

        if (contact) {
            return (
                <p>
                    {`Questions can be directed to ${contact}`}
                </p>
            );
        }

        return null;
    }

    buildField = (fieldName) => {
        const fieldSettings = this.state.settings.form.fields[fieldName];
        const errors = this.state.errors || {};

        if (fieldSettings && isSettingEnabled(fieldSettings.show)) {
            return (
                <Fragment key={fieldName}>
                    <label>{fieldSettings.label}
                        <input
                            onChange={this.onFieldChangeGenerator(fieldName)}
                            value={this.state.attendee[fieldName]}
                        />
                        <div className="validation-error">{errors[fieldName]}</div>
                    </label>
                </Fragment>
            );
        }

        return null;
    }

    getRemainingSpots = () => {
        const {vendor, reservations} = this.state;

        if (vendor && reservations) {
            return vendor.allowedReservationCount - reservations.length;
        }

        return null;
    }

    hasRemainingSpots = () => {
        const remainingSpots = this.getRemainingSpots();

        return remainingSpots && remainingSpots > 0;
    }

    enforceReservationLimit = () => isSettingEnabled(this.state.settings.enforceReservationLimit);

    isParticipantEditable = () => !this.enforceReservationLimit() || this.hasRemainingSpots() || this.state.editing;

    render() {
        const details = this.state.settings;

        return this.state.loading
            ? <Loading />
            : (
                <DocumentTitle title={`${details.window.title}`}>
                    <main>
                        <p>
                            <img
                                alt="header"
                                className="header-img"
                                src={details.banner.src}
                            />
                        </p>

                        <h3 className="hy-vee-red">{details.year}{' '}{details.title}</h3>
                        <p>{details.description}</p>

                        <div className="eventDetails">
                            <h6 className="hy-vee-red">{'Details:'}</h6>
                            <p>
                                {'Date/Time: '}
                                <span className="heavy">
                                    {details.date}
                                </span>
                            </p>

                            <p>
                                {'Location: '}
                                <span className="heavy">
                                    {details.venue.name}{'. '}{details.venue.address}
                                </span>
                            </p>

                            <p className="heavy">
                                {details.due.label}{' '}<span className="hy-vee-red">{details.due.value}</span>
                            </p>
                            {this.questionsSection()}
                            {this.inquirySection()}
                        </div>

                        <div className="content-container">
                            <div>
                                <h6 className="hy-vee-red">{details.form.title}{':'}</h6>
                                <div className="vendor-info">
                                    <div>
                                        <div className="field-label heavy">{details.form.orgTypeText}</div>
                                        {this.state.attendee.vendorName}
                                    </div>
                                    <SpotsSection
                                        enforceReservationLimit={this.enforceReservationLimit()}
                                        totalSpots={this.state.vendor.allowedReservationCount}
                                    />

                                </div>
                                {this.isParticipantEditable() ?
                                    <form onSubmit={this.onSubmit}>
                                        {[
                                            'firstName',
                                            'lastName',
                                            'textField1',
                                            'address',
                                            'city',
                                            'state',
                                            'zip',
                                            'email',
                                            'phone',
                                            'textField2'
                                        ].map(this.buildField)}
                                        <Legal legal={details.form.legal} />
                                        {this.agreeToAbideSection()}
                                        <button
                                            disabled={this.formIncomplete()}
                                            type="submit"
                                        >
                                            {details.form.button.text}
                                            {this.state.rsvpInProgress && <span className="fa fa-spinner fa-spin" />}
                                        </button>
                                    </form>
                                    : null
                                }
                            </div>
                            <ReservationList
                                reservations={this.state.reservations}
                                remainingSpots={this.getRemainingSpots()}
                                showReservationList={this.state.settings.showReservationList}
                                allowReservationChanges={this.state.settings.allowReservationChanges}
                                changeReservationFunction={this.editAttendee}
                            />
                        </div>
                    </main>
                </DocumentTitle>
            );
    }
}
EventPage.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object
};
export default EventPage;



// WEBPACK FOOTER //
// ./src/event/EventPage.js